<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { AccessPermission } from '@/api';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCreateButton from '@/components/design/base/GaiaCreateButton.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaSegmented from '@/components/design/base/GaiaSegmented.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import GaiaSelectRow from '@/components/design/composite/GaiaSelectRow.vue';
import { useOrganizationStore } from '@/stores/organizationStore';
import { ORG_WIDE_ACCESS_OPTIONS } from '@/utils/accessPermission';

/**
 * Self-service "Create organisation" dialog — the way out of the org-less
 * state, and a plain "new workspace" action for anyone the deployment allows
 * (`userPermissions.can_create_organisation`). Opened from the sidebar's
 * workspace switcher and from the org-less home.
 */
const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits<{ 'update:show': [value: boolean] }>();

const organizationStore = useOrganizationStore();

const showModal = computed({
  get: () => props.show,
  set: (value: boolean) => {
    emit('update:show', value);
  },
});

const name = ref('');
const description = ref('');
const hostingZone = ref('eu');
const membersPartiesAccess = ref<AccessPermission>(AccessPermission._0);
const membersContractsAccess = ref<AccessPermission>(AccessPermission._0);
const nameError = ref<string | undefined>(undefined);
const submitting = ref(false);
const submitError = ref<string | null>(null);

// Same two zones as the sign-up profile step (SignUpCompleteView), which is
// where an organisation otherwise gets its hosting zone.
const HOSTING_ZONE_OPTIONS = [
  { label: 'EU', value: 'eu' },
  { label: 'US', value: 'us' },
];

function reset(): void {
  name.value = '';
  description.value = '';
  hostingZone.value = 'eu';
  membersPartiesAccess.value = AccessPermission._0;
  membersContractsAccess.value = AccessPermission._0;
  nameError.value = undefined;
  submitError.value = null;
}

// A reopened dialog starts blank; a half-typed name from a cancelled attempt
// would read as something the app remembered on purpose.
watch(showModal, open => {
  if (!open) reset();
});

async function submit(): Promise<void> {
  const trimmedName = name.value.trim();
  if (!trimmedName) {
    nameError.value = 'Please enter a name';
    return;
  }
  nameError.value = undefined;
  submitError.value = null;
  submitting.value = true;

  const created = await organizationStore.createOwnOrganization({
    name: trimmedName,
    description: description.value.trim() || null,
    hosting_zone: hostingZone.value,
    member_access: {
      all_contractual_parties_access: membersPartiesAccess.value,
      all_contracts_access: membersContractsAccess.value,
    },
  });

  if (!created) {
    submitting.value = false;
    submitError.value = organizationStore.error ?? 'Failed to create organisation';
    return;
  }

  // The backend has already switched the user into the new organisation, so
  // this is a switch in every way that matters: every request the app makes
  // is re-scoped, and a full document load onto home is the cheapest correct
  // way to drop the cached state of the old (or no) organisation. `submitting`
  // stays on so the dialog does not flash back to idle before the reload.
  window.location.assign('/');
}
</script>

<template>
  <gaia-modal v-model:show="showModal" width="480px" :closable="!submitting">
    <template #header>
      <gaia-text size="subtitle" weight="semibold" tone="ink">Create organisation</gaia-text>
    </template>

    <n-flex vertical :size="18">
      <gaia-text size="body" tone="subtle">
        You will be its administrator and switch into it right away.
      </gaia-text>
      <gaia-banner plain type="warning">
        This creates a separate, new organisation. To join one that already exists, ask its
        administrator for an invitation.
      </gaia-banner>

      <gaia-form-field label="Name" required :error="nameError">
        <gaia-input
          v-model:value="name"
          size="large"
          placeholder="Lindqvist & Partners"
          :disabled="submitting"
          :input-props="{ autocomplete: 'organization' }"
          @keyup.enter="submit"
        />
      </gaia-form-field>

      <gaia-form-field label="Description" hint="Optional">
        <gaia-textarea
          v-model:value="description"
          placeholder="What this organisation is for"
          :disabled="submitting"
          :autosize="{ minRows: 2, maxRows: 4 }"
        />
      </gaia-form-field>

      <gaia-form-field label="Hosting zone">
        <gaia-segmented
          v-model:value="hostingZone"
          block
          size="medium"
          :options="HOSTING_ZONE_OPTIONS"
          :disabled="submitting"
        />
      </gaia-form-field>

      <gaia-form-field label="Member access" hint="You can change this later on the member role">
        <n-flex vertical :size="8">
          <gaia-select-row
            card
            label="Access to all contractual parties"
            hint="On top of the parties shared with each member individually"
            :value="membersPartiesAccess"
            :options="ORG_WIDE_ACCESS_OPTIONS"
            :disabled="submitting"
            @update:value="
              (value: string | number) => (membersPartiesAccess = value as AccessPermission)
            "
          />
          <gaia-select-row
            card
            label="Access to all contracts"
            hint="On top of the contracts shared with each member individually"
            :value="membersContractsAccess"
            :options="ORG_WIDE_ACCESS_OPTIONS"
            :disabled="submitting"
            @update:value="
              (value: string | number) => (membersContractsAccess = value as AccessPermission)
            "
          />
        </n-flex>
      </gaia-form-field>

      <gaia-banner v-if="submitError" plain type="error">{{ submitError }}</gaia-banner>
    </n-flex>

    <template #action>
      <n-flex justify="flex-end" :size="8">
        <gaia-button size="small" :disabled="submitting" @click="showModal = false"
          >Cancel</gaia-button
        >
        <gaia-create-button
          subject="organisation"
          size="sm"
          :icon="false"
          :loading="submitting"
          @click="submit"
        />
      </n-flex>
    </template>
  </gaia-modal>
</template>
